import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Stack, Tab, styled, alpha } from '@mui/material';
import {
  EconsultCaseConsultActionDetails,
  EconsultCaseHeader,
  EconsultCaseNotes,
  EconsultClinicalReportsDetails,
  hasLabReportSections,
  hasEncounterSections,
  hasCumulativeProfileSections,
  EconsultUserService,
  useAuth
} from '@oh-vcp/components-web';
import {
  ConsultTab,
  NoteTypes,
  NoteActionTypes,
  EconsultUserRoles,
  NoteStatusCodes,
  EconsultActionTab,
  CaseNote,
  NoteParticipantStatusReasons,
  SelectedNotesTab,
  EconsultUtils
} from '@oh-vcp/components-common';
import { useEconsultCaseDetailsContext } from '@oh-vcp/components-web/src/econsultCaseDetailsContext/EconsultCaseDetailsContext';
import useTimeOptions from '../../api/useTimeOptions';
import CaseService from '@oh-vcp/components-web/src/services/caseService';
import { useParams } from 'react-router-dom';
import { useEconsultTabContext } from '@oh-vcp/components-web/src/econsultTabContext/EconsultTabContext';
import { useTranslation } from 'react-i18next';
import {
  AskMoreInfoActiveIcon,
  AskMoreInfoInactiveIcon,
  CancelActiveIcon,
  CancelInactiveIcon,
  CloseCaseActiveIcon,
  CloseCaseInactiveIcon,
  DeclineActiveIcon,
  DeclineInactiveIcon,
  ReassignActiveIcon,
  ReassignInactiveIcon,
  WriteConsultActiveIcon,
  WriteConsultInactiveIcon
} from '@oh-vcp/components-ui';

interface EconsultCaseConsultDetailsViewProps {
  setPreviousDraftNote: (prevDraftNote: CaseNote | undefined) => void;
  getPreviousDraftNote: () => CaseNote | undefined;
  isDraftChanged: () => boolean | false;
}
const EconsultCaseConsultDetailsView: FC<EconsultCaseConsultDetailsViewProps> = ({
  setPreviousDraftNote,
  getPreviousDraftNote,
  isDraftChanged
}) => {
  const [tabOptions, setTabOptions] = useState<EconsultActionTab[]>([]);
  const [isPdfEnabled, setIsPdfEnabled] = useState<boolean>(false);
  const [isEmrEnabled, setIsEmrEnabled] = useState<boolean>(false);
  const [selectedNoteTab, setSelectedNoteTab] = useState<SelectedNotesTab>(SelectedNotesTab.Notes);
  const { caseDetails, setTimeOptions, userRole, userRoleDual, setUserRoleDual, assignerPrograms } =
    useEconsultCaseDetailsContext();
  // const [selectedConsultTab, setSelectedConsultTab] = useState<ConsultTab | undefined>(undefined);
  const { selectedTab, setSelectedTab } = useEconsultTabContext();
  const { data: options } = useTimeOptions();
  const { caseId } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const curUserRole = userRoleDual || userRole;

  useEffect(() => {
    if (options) {
      setTimeOptions(options);
    }
  }, [options, setTimeOptions]);
  useEffect(() => {
    return () => {
      setUserRoleDual(undefined);
      console.log('EconsultCaseConsultDetailsView unmounted');
    };
  }, []);

  useEffect(() => {
    console.log(`userRole =  ${userRoleDual}, ${userRole}, ${curUserRole}`);
    const addNote: EconsultActionTab = {
      label: t('Case.consult.addNote'),
      tab: ConsultTab.AddNote,
      imageSrc:
        selectedTab === ConsultTab.AddNote ? WriteConsultActiveIcon : WriteConsultInactiveIcon,
      imageAltText: 'Add note icon',
      iconPosition: 'start',
      role: ''
    };

    const provideConsult: EconsultActionTab = {
      label: t('Case.consult.provideConsult'),
      tab: ConsultTab.Provided,
      imageSrc:
        selectedTab === ConsultTab.Provided ? WriteConsultActiveIcon : WriteConsultInactiveIcon,
      imageAltText: 'Provide consult icon',
      iconPosition: 'start',
      role: ''
    };

    const requestMoreInfo: EconsultActionTab = {
      label: t('Case.consult.requestMoreInfo'),
      tab: ConsultTab.MoreInfo,
      imageSrc:
        selectedTab === ConsultTab.MoreInfo ? AskMoreInfoActiveIcon : AskMoreInfoInactiveIcon,
      imageAltText: 'More info icon',
      iconPosition: 'start',
      role: ''
    };

    const cancel: EconsultActionTab = {
      label: t('Case.consult.cancel'),
      tab: ConsultTab.Cancel,
      imageSrc: selectedTab === ConsultTab.Cancel ? CancelActiveIcon : CancelInactiveIcon,
      imageAltText: 'Cancel icon',
      iconPosition: 'start',
      role: ''
    };

    const returnConsult: EconsultActionTab = {
      label: t('Case.consult.returnConsult'),
      tab: ConsultTab.Returned,
      imageSrc: selectedTab === ConsultTab.Returned ? DeclineActiveIcon : DeclineInactiveIcon,
      imageAltText: 'Return consult icon',
      iconPosition: 'start',
      role: ''
    };

    const complete: EconsultActionTab = {
      label: t('Case.consult.complete'),
      tab: ConsultTab.Complete,
      imageSrc: selectedTab === ConsultTab.Complete ? CloseCaseActiveIcon : CloseCaseInactiveIcon,
      imageAltText: 'Complete consult icon',
      iconPosition: 'start',
      role: ''
    };

    const requestClarification: EconsultActionTab = {
      label: t('Case.consult.requestClarification'),
      tab: ConsultTab.RequestClarification,
      imageSrc:
        selectedTab === ConsultTab.RequestClarification
          ? WriteConsultActiveIcon
          : WriteConsultInactiveIcon,
      imageAltText: 'Request clarification icon',
      iconPosition: 'start',
      role: ''
    };

    const redirect: EconsultActionTab = {
      label: t('Case.consult.redirect'),
      tab: ConsultTab.Redirect,
      imageSrc: selectedTab === ConsultTab.Redirect ? ReassignActiveIcon : ReassignInactiveIcon,
      imageAltText: 'Redirect icon',
      iconPosition: 'start',
      role: ''
    };

    const provideMoreInfo: EconsultActionTab = {
      label: t('Case.consult.provideMoreInfo'),
      tab: ConsultTab.ProvideMoreInfo,
      imageSrc:
        selectedTab === ConsultTab.ProvideMoreInfo
          ? WriteConsultActiveIcon
          : WriteConsultInactiveIcon,
      imageAltText: 'Provide more info icon',
      iconPosition: 'start',
      role: ''
    };

    const isSpecialistProvidedConsultAction = () => {
      if (
        !caseDetails ||
        (curUserRole !== EconsultUserRoles.consultant &&
          curUserRole !== EconsultUserRoles.consultantDelegate)
      ) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }
      return (
        lastActiveNote.actionTypeCd === NoteActionTypes.writeConsult ||
        lastActiveNote.actionTypeCd === NoteActionTypes.recomplete
      );
    };

    const isReferrerReopenedCaseAction = () => {
      if (
        !caseDetails ||
        (curUserRole !== EconsultUserRoles.referrer &&
          curUserRole !== EconsultUserRoles.referrerDelegate)
      ) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }
      return (
        caseDetails.statusCd !== NoteStatusCodes.closed &&
        caseDetails.participant?.statusCd !== NoteStatusCodes.closed &&
        caseDetails.participant?.completedOn !== undefined &&
        CaseService.containsConsultProvidedNote(caseDetails) &&
        lastActiveNote.actionTypeCd !== NoteActionTypes.askForMoreInfo &&
        lastActiveNote.actionTypeCd !== NoteActionTypes.decline
      );
    };

    const isReferrerRequestClarificationActionHidden = function () {
      if (!caseDetails) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }
      return (
        lastActiveNote.actionTypeCd === NoteActionTypes.decline &&
        caseDetails.participant?.statusReason ===
          NoteParticipantStatusReasons.consultantUnavailable &&
        CaseService.containsConsultProvidedNote(caseDetails)
      );
    };

    const isReferrerResolveCompletedConsultAction = function () {
      if (
        !caseDetails ||
        (curUserRole !== EconsultUserRoles.referrer &&
          curUserRole !== EconsultUserRoles.referrerDelegate)
      )
        return false;
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote || caseDetails.statusCd === NoteStatusCodes.closed) {
        return false;
      }
      if (
        lastActiveNote.actionTypeCd === NoteActionTypes.writeConsult ||
        lastActiveNote.actionTypeCd === NoteActionTypes.recomplete
      ) {
        return true;
      }
      return (
        lastActiveNote.actionTypeCd === NoteActionTypes.decline &&
        CaseService.containsConsultProvidedNote(caseDetails)
      );
    };

    const isReferrerResolveDeclinedConsultAction = () => {
      if (
        !caseDetails ||
        (curUserRole !== EconsultUserRoles.referrer &&
          curUserRole !== EconsultUserRoles.referrerDelegate)
      ) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }
      if (
        lastActiveNote.actionTypeCd === NoteActionTypes.decline &&
        CaseService.containsConsultProvidedNote(caseDetails)
      ) {
        return false;
      }
      return (
        lastActiveNote.actionTypeCd === NoteActionTypes.decline ||
        lastActiveNote.actionTypeCd === NoteActionTypes.unassign
      );
    };

    const isReferrerUpdateSentCaseAction = () => {
      if (
        !caseDetails ||
        (curUserRole !== EconsultUserRoles.referrer &&
          curUserRole !== EconsultUserRoles.referrerDelegate)
      ) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }
      return (
        !CaseService.isCaseCompleted(caseDetails) &&
        caseDetails.statusCd !== NoteStatusCodes.closed &&
        caseDetails.participant?.statusCd !== NoteStatusCodes.closed &&
        caseDetails.participant?.completedOn === undefined &&
        (caseDetails.participant?.program === undefined ||
          caseDetails.participant?.consultant === undefined) &&
        !CaseService.hasSpecialistResponded(caseDetails) &&
        lastActiveNote.actionTypeCd !== NoteActionTypes.decline &&
        lastActiveNote.actionTypeCd !== NoteActionTypes.unassign
      );
    };

    const isReferrerUpdateTriagedCaseAction = () => {
      if (
        !caseDetails ||
        (curUserRole !== EconsultUserRoles.referrer &&
          curUserRole !== EconsultUserRoles.referrerDelegate)
      ) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }

      if (
        CaseService.isCaseCompleted(caseDetails) ||
        caseDetails.statusCd === NoteStatusCodes.closed ||
        caseDetails.participant?.statusCd === NoteStatusCodes.closed ||
        caseDetails.participant?.completedOn ||
        !caseDetails.participant?.program ||
        !caseDetails.participant?.consultant
      ) {
        return false;
      }

      return lastActiveNote.actionTypeCd === NoteActionTypes.assign;
    };

    const isReferrerUpdateRepliedCaseAction = () => {
      if (
        !caseDetails ||
        (curUserRole !== EconsultUserRoles.referrer &&
          curUserRole !== EconsultUserRoles.referrerDelegate)
      ) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }

      if (
        CaseService.isCaseCompleted(caseDetails) ||
        caseDetails.statusCd === NoteStatusCodes.closed ||
        caseDetails.participant?.statusCd === NoteStatusCodes.closed ||
        caseDetails.participant?.completedOn ||
        !caseDetails.participant?.consultant
      ) {
        return false;
      }

      return (
        lastActiveNote.actionTypeCd === NoteActionTypes.assign ||
        (lastActiveNote.actionTypeCd === NoteActionTypes.reply &&
          !CaseService.containsConsultProvidedNote(caseDetails))
      );
    };

    const isReferrerSendMoreInfoAction = () => {
      if (
        !caseDetails ||
        (curUserRole !== EconsultUserRoles.referrer &&
          curUserRole !== EconsultUserRoles.referrerDelegate)
      ) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }
      return (
        lastActiveNote.actionTypeCd === NoteActionTypes.askForMoreInfo &&
        !CaseService.containsConsultProvidedNote(caseDetails)
      );
    };

    const isSpecialistAddNoteAction = () => {
      if (
        !caseDetails ||
        (curUserRole !== EconsultUserRoles.consultant &&
          curUserRole !== EconsultUserRoles.consultantDelegate)
      ) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }
      return lastActiveNote.actionTypeCd === NoteActionTypes.askForMoreInfo;
    };

    const isUserTriageAssignerInProgram = function (programId: number | undefined) {
      var program = assignerPrograms?.triageAssigner?.find((id: number) => id === programId);
      if (program) return true;
      return false;
    };

    const isCurrentUserCaseTriageAdmin = () => {
      return NoteTypes.triageAdmin === curUserRole;
    };

    const isCurrentUserCaseTriageAssigner = () => {
      if (!isCurrentUserCaseTriageAdmin()) {
        return false;
      }
      const programId = caseDetails?.participant?.program?.programId
        ? parseInt(caseDetails.participant.program.programId)
        : undefined;
      return isUserTriageAssignerInProgram(programId);
    };

    const isTriageRedirect = () => {
      if (!caseDetails || !isCurrentUserCaseTriageAdmin()) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }

      return lastActiveNote.actionTypeCd === NoteActionTypes.unassign;
    };

    const isTriageAssignerRedirect = () => {
      if (!caseDetails || !isCurrentUserCaseTriageAssigner()) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }

      return lastActiveNote.actionTypeCd === NoteActionTypes.unassign;
    };

    const isTriageAssignerRedirectAndCancel = () => {
      if (!caseDetails || !isCurrentUserCaseTriageAssigner()) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }

      return lastActiveNote.actionTypeCd === NoteActionTypes.send;
    };

    const isReferrerSendMoreInfoNoCancelAction = () => {
      if (
        !caseDetails ||
        !user ||
        (curUserRole !== EconsultUserRoles.referrer &&
          curUserRole !== EconsultUserRoles.referrerDelegate)
      ) {
        return false;
      }
      var lastActiveNote = CaseService.getLastActiveNote(caseDetails);
      if (!lastActiveNote) {
        return false;
      }
      return (
        lastActiveNote.actionTypeCd === NoteActionTypes.askForMoreInfo &&
        CaseService.containsConsultProvidedNote(caseDetails)
      );
    };
    if (CaseService.isCaseCompleted(caseDetails)) {
      setTabOptions([]);
    } else {
      if (CaseService.isSpecialistRespondAction(caseDetails, curUserRole)) {
        setTabOptions([provideConsult, requestMoreInfo, returnConsult]);
      } else if (isReferrerResolveCompletedConsultAction()) {
        if (!isReferrerRequestClarificationActionHidden()) {
          setTabOptions([complete, requestClarification]);
        } else {
          setTabOptions([complete]);
        }
      } else if (isSpecialistProvidedConsultAction() || isReferrerReopenedCaseAction()) {
        if (isSpecialistProvidedConsultAction()) {
          addNote.role = 'consultant';
        } else {
          addNote.role = 'referrer';
        }
        setTabOptions([addNote]);
      } else if (isReferrerResolveDeclinedConsultAction()) {
        setTabOptions([redirect, cancel]);
      } else if (isReferrerUpdateSentCaseAction()) {
        setTabOptions([addNote, redirect, cancel]);
      } else if (isReferrerUpdateTriagedCaseAction() || isReferrerUpdateRepliedCaseAction()) {
        setTabOptions([cancel, addNote]);
      } else if (isReferrerSendMoreInfoAction()) {
        setTabOptions([cancel, provideMoreInfo]);
      } else if (isSpecialistAddNoteAction()) {
        setTabOptions([provideConsult, addNote, returnConsult]);
      } else if (
        EconsultUserService.isDepartmentalOrTriageAssignerAction(
          caseDetails,
          curUserRole,
          assignerPrograms
        ) ||
        isTriageAssignerRedirectAndCancel()
      ) {
        setTabOptions([redirect, cancel]);
      } else if (
        isTriageRedirect() ||
        isTriageAssignerRedirect() ||
        EconsultUserService.isTriageRedirectConsultAction(caseDetails, curUserRole)
      ) {
        setTabOptions([redirect]);
      } else if (isReferrerSendMoreInfoNoCancelAction()) {
        setTabOptions([provideMoreInfo]);
      } else {
        setTabOptions([]);
      }
    }
    setIsPdfEnabled(!isCurrentUserCaseTriageAdmin() || isCurrentUserCaseTriageAssigner());
    const isRequester =
      curUserRole === EconsultUserRoles.referrer ||
      curUserRole === EconsultUserRoles.referrerDelegate;
    const isHaloCaseForRequester =
      isRequester && EconsultUtils.isSmartOnFhirCase(caseDetails?.externalReferralId);
    if (
      !caseDetails?.patient?.noOhip &&
      caseDetails?.patient?.ohipNumber &&
      !isHaloCaseForRequester &&
      (curUserRole === EconsultUserRoles.consultant ||
        curUserRole === EconsultUserRoles.consultantDelegate ||
        curUserRole === EconsultUserRoles.referrer ||
        curUserRole === EconsultUserRoles.referrerDelegate ||
        user?.econsult?.caseConsultOn ||
        user?.econsult?.caseCreate) &&
      caseDetails.participant &&
      caseDetails.participant.consultant
    ) {
      setIsEmrEnabled(true);
    } else {
      setIsEmrEnabled(false);
    }
  }, [caseDetails, curUserRole, assignerPrograms, user, t, selectedTab]);

  if (!caseDetails || caseDetails.caseId?.toString() !== caseId) {
    return null;
  }
  const smallScreen = EconsultUtils.isMobileDevice();
  const tabMinWidth = smallScreen ? 76 : 150;
  const tabHeight = smallScreen ? 48 : 36;
  const investTabLabel = smallScreen ? 'Investi gations' : 'Investigations';
  const StyledTab = styled(Tab)(({ theme }) => {
    const primary = theme.palette.primary.main;
    const divider = theme.palette.divider;

    return {
      textTransform: 'none',
      width: smallScreen ? tabMinWidth : 'auto',
      minWidth: tabMinWidth,
      fontSize: smallScreen ? 11 : 12,
      fontWeight: 500,
      lineHeight: 1,
      height: tabHeight,
      minHeight: tabHeight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: smallScreen ? '4px 8px' : '4px 20px',
      boxSizing: 'border-box',
      borderRadius: '10px 10px 0 0',
      color: primary,
      backgroundColor: theme.palette.common.white,
      borderStyle: 'solid',
      borderWidth: '1px 1px 1px 1px',
      borderColor: divider,
      marginLeft: -1,
      '&:first-of-type': { marginLeft: 0 },

      '&:hover': {
        backgroundColor: primary,
        color: theme.palette.common.white
      },

      '&.Mui-selected': {
        backgroundColor: theme.palette.common.white,
        color: primary,
        borderTopColor: primary,
        borderLeftColor: primary,
        borderRightColor: primary,
        borderBottom: 'none',
        marginBottom: '-1px',
        zIndex: 3
      },

      '&.Mui-focusVisible': { outline: 'none' }
    };
  });

  const hanleSelectedNoteTabChanged = (
    event: SyntheticEvent<Element, Event>,
    tab: SelectedNotesTab
  ) => {
    setSelectedNoteTab(tab);
  };

  const isCurrentUserIsDepartmentAssigner =
    EconsultUserService.isCurrentUserCaseDepartmentalAssigner(
      userRole,
      assignerPrograms,
      caseDetails.participant?.program?.programId
    );

  const isTriageAssigner = EconsultUserService.isCurrentUserCaseTriageAssigner(
    userRole,
    assignerPrograms,
    caseDetails.participant?.program?.programId
  );

  const externalReferralSections = caseDetails?.externalReferralContent
    ? JSON.parse(caseDetails.externalReferralContent)
    : null;

  const hasLabReports = hasLabReportSections(externalReferralSections);

  const hasEncounters = hasEncounterSections(externalReferralSections);

  const hasPatientProfile = hasCumulativeProfileSections(externalReferralSections);

  return (
    <>
      <Stack direction="column" p={4}>
        <EconsultCaseHeader isPdfEnabled={isPdfEnabled} isEmrEnabled={isEmrEnabled} />
        {externalReferralSections &&
        Object.keys(externalReferralSections).length > 0 &&
        (hasLabReports || hasPatientProfile || hasEncounters) &&
        (userRole === EconsultUserRoles.consultant ||
          userRole === EconsultUserRoles.consultantDelegate ||
          userRole === EconsultUserRoles.referrer ||
          userRole === EconsultUserRoles.referrerDelegate ||
          isTriageAssigner) &&
        !isCurrentUserIsDepartmentAssigner ? (
          <TabContext value={selectedNoteTab}>
            <TabList
              TabIndicatorProps={{ style: { display: 'none' } }}
              onChange={hanleSelectedNoteTabChanged}>
              <StyledTab label={'Case Details'} value={SelectedNotesTab.Notes} />
              {hasLabReports && (
                <StyledTab label={investTabLabel} value={SelectedNotesTab.LabReports} />
              )}
              {hasPatientProfile && (
                <StyledTab label={'Patient Profile'} value={SelectedNotesTab.Clinical} />
              )}
              {hasEncounters && (
                <StyledTab label={'Clinical Notes'} value={SelectedNotesTab.Encounters} />
              )}
            </TabList>
            <TabPanel value={SelectedNotesTab.Notes}>
              <EconsultCaseConsultActionDetails
                options={tabOptions}
                setPreviousDraftNote={setPreviousDraftNote}
                getPreviousDraftNote={getPreviousDraftNote}
                isDraftChanged={isDraftChanged}
              />
              <EconsultCaseNotes />
            </TabPanel>
            <TabPanel value={SelectedNotesTab.LabReports}>
              <EconsultClinicalReportsDetails group="labReports" />
            </TabPanel>
            <TabPanel value={SelectedNotesTab.Clinical}>
              <EconsultClinicalReportsDetails />
            </TabPanel>
            <TabPanel value={SelectedNotesTab.Encounters}>
              <EconsultClinicalReportsDetails group="encounters" />
            </TabPanel>
          </TabContext>
        ) : (
          <>
            <EconsultCaseConsultActionDetails
              options={tabOptions}
              setPreviousDraftNote={setPreviousDraftNote}
              getPreviousDraftNote={getPreviousDraftNote}
              isDraftChanged={isDraftChanged}
            />
            <EconsultCaseNotes />
          </>
        )}
      </Stack>
    </>
  );
};

export default EconsultCaseConsultDetailsView;
